/**
 * Created by fers on 26.09.15.
 */

import { v4 as uuidv4 } from 'uuid';
import { declareLinkedEntity } from '../fw/MetaDataHelper.ts';

export default declareLinkedEntity({
    entity: "dataSources",
    graphQL: "DataSource",
    name: "Data Sources",
    parentRelation: "dataSources",
    storageType: 'central',
	defaultOrder: [['idx', 'ASC'], 'name', 'id'],
    defaultTitle: (data) => {
        return data['name'];
    },
    writePermissionFromParent: true,
    isPublic: true,
    fields: (types) => { return {
        id: {
            name: "DataSource ID",
            type: "id",
            defaultValue: () => {
                return uuidv4();
            },
            primaryKey: true,
            required: true,
            userEditable: true,
            isPublic: true,
        },
        name: {
            name: "Name",
            type: "string",
            trim: true,
            isPublic: true,
        },
        description: {
            name: "Description",
            type: "string",
            trim: true,
            isPublic: true,
        },
        typeId: {
            name: "Type ID",
            description: "Type ID that allows to find existing datasources in query",
            type: "string",
            isPublic: true,
        },
        type: {
            name: "Type",
            type: "string",
            trim: true,
            defaultValue: "query",
            required: true,
            isPublic: true,
        },
		idx: {
			name: "Version",
			type: "int",
			defaultValue: 1,
            isPublic: true,
		},
        args: {
            name: "Arguments",
            type: "object",
            defaultValue: {},
            required: true,
            isPublic: true,
        },
        relativeTo: {
            name: "DataSource Relative To",
            type: "relation",
            to: () => types['dataSources'],
            relativeTo: ['screen', 'screenDef', 'scenarioScreenDef', 'scenarioDef'],
            isPublic: true,
        },
        primaryEntityDef: {
            name: "Entity Definition",
            type: "relation",
            to: () => types['entityDefs'],
            isPublic: true,
            userEditable: false,
            import: false,
            export: false,
            controllerFn: 'getPrimaryEntityDef',
        },
        channelDefKeys: {
            name: "Attached Application Definition Key",
            type: "stringArray",
            export: false,
            controllerFn: 'getChannelDefKeys',
            isPublic: true,
            userEditable: false,
        },
        inputMapping: {
            name: "Input Mapping",
            type: "object",
            defaultValue: {},
            isPublic: true,
        },
        screen: {
            name: "Screen",
            type: "relation",
            to: () => types['screens'],
            parent: true, /* we are using multiple (variable) parents */
            compositeKey: true,
            hiddenCreate: true,
        },
        screenDef: {
            name: "Screen Definition",
            type: "relation",
            to: () => types['screenDefs'],
            parent: true, /* we are using multiple (variable) parents */
            compositeKey: true,
            hiddenCreate: true,
        },
        scenarioScreenDef: {
            name: "Screen Definition",
            type: "relation",
            to: () => types['scenarioScreenDefs'],
            parent: true, /* we are using multiple (variable) parents */
            compositeKey: true,
            hiddenCreate: true,
            relativeTo: ['scenarioDef'],
            isPublic: true,
        },
        scenarioDef: {
            name: "Scenario Definition",
            type: "relation",
            to: () => types['scenarioDefs'],
            hiddenCreate: true,
            compositeKey: true,
            isPublic: true,
        },
        checkValidity: {
            name: "Is DataSource valid?",
            type: "boolean",
            export: false,
            controllerFn: 'checkValidity',
            userEditable: false,
        }
    }; },
    relations: (types) => { return {
        entitySelectionRules: {
            type: 'oneToMany',
            to: types['entitySelectionRules'],
            hasPaging: false,
            export: true,
            inlineStore: true,
            defaultValue: () => {
                return {};
            }
        },
        helperSelectionRules: {
            type: 'oneToMany',
            to: types['entitySelectionRules'],
            hasPaging: false,
            export: true,
            inlineStore: true,
            defaultValue: () => {
                return {};
            }
        },
        entityDefs: {
            name: "Entity Definition",
            type: 'oneToMany',
            hasPaging: false,
            export: false,
            to: types['entityDefs'],
            controllerFn: 'getEntityDefs',
            isPublic: true,
            userEditable: false,
        },
        mappedFields: {
            name: "All Mapped fields",
            type: 'oneToMany',
            hasPaging: false,
            export: false,
            to: types['fieldDefs'],
            controllerFn: 'getAllMappedFields',
            userEditable: false,
        },
        channels: {
            name: "Attached Application",
            type: 'oneToMany',
            hasPaging: false,
            export: false,
            controllerFn: 'getChannels',
            to: types['channels'],
            userEditable: false,
        },
        helperChannels: {
            name: "Attached Helper Application",
            type: 'oneToMany',
            hasPaging: false,
            export: false,
            controllerFn: 'getHelperChannels',
            to: types['channels'],
            userEditable: false,
        },
        helperChannelDefs: {
            name: "Attached Application Definition",
            type: 'oneToMany',
            hasPaging: false,
            export: false,
            controllerFn: 'getHelperChannelDefs',
            to: types['channelDefs'],
            userEditable: false,
            isPublic: true,
        },
        channelDefs: {
            name: "Attached Application Definition",
            type: 'oneToMany',
            hasPaging: false,
            export: false,
            controllerFn: 'getChannelDefs',
            to: types['channelDefs'],
            userEditable: false,
            isPublic: true,
        },
        boxesToAdd: {
            type: 'oneToMany',
            hasPaging: false,
            controllerFn: 'getBoxesToAdd',
            to: types['screenBoxToAdd'],
        },
        dataSourcesToAdd: {
            type: 'oneToMany',
            hasPaging: false,
            controllerFn: 'getDataSourcesToAdd',
            to: types['screenDataSourceToAdd'],
        },
        allPossibleRelations: {
            type: 'oneToMany',
            to: types['entitySelectionRules'],
            description: "Returns entitySelectionRules for all possible relations that can be queried",
            controllerFn: 'getAllPossibleRelations',
            export: false,
        },
        relatedActionDefs: {
            name: "Related Action Defs",
            type: 'oneToMany',
            hasPaging: false,
            export: false,
            to: types['actionDefs'],
            controllerFn: 'getRelatedActionDefs',
            userEditable: false,
        },
    }; }
});
